import { type Dispatch, type SetStateAction, useEffect, useId, useState } from "react";

const broadcast = new BroadcastChannel("local-storage");
const local = new Map<string, Set<{ key: string; id: string; f: (value: unknown) => void }>>();

export const useLocalStorageState = <T>(key: string, defaultValue: T | (() => T)): [T, Dispatch<SetStateAction<T>>] => {
  const id = useId();
  const [value, setValue] = useState(() => {
    try {
      const text = localStorage.getItem(key) as string;
      if (text === null) throw "";
      const value = JSON.parse(localStorage.getItem(key) as string) as {
        value: T;
      };
      if (value === undefined) throw "";
      return value.value;
    } catch (_error) {
      return typeof defaultValue !== "function" ? defaultValue : (defaultValue as () => T)();
    }
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify({ value }));
    broadcast.postMessage({ key, value, id });
    local.get(key)?.forEach((s) => key === s.key && id !== s.id && s.f(value));
  }, [value, key, id]);

  useEffect(() => {
    const sub = { key, id, f: (x: unknown) => setValue(x as T) };
    if (!local.has(key)) local.set(key, new Set());
    local.get(key)?.add(sub);
    const onBroadcast = (e: MessageEvent) => {
      if (e.data.key === key && e.data.id !== id) setValue(e.data.value);
    };
    broadcast.addEventListener("message", onBroadcast);
    return () => {
      broadcast.removeEventListener("message", onBroadcast);
      local.get(key)?.delete(sub);
      if (local.get(key)?.size === 0) local.delete(key);
    };
  }, [key, id]);

  return [value, setValue] as const;
};
